import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatNumber } from "../utils/formatNumber";

const N = (value) => Number(value ?? 0) || 0;

const createEntry = (item) => ({
  id:
    window.crypto?.randomUUID?.() ??
    `destination-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: item?.productName || item?.product?.title || "",
  pricePerUnit: N(item?.pricePerUnit),
  weight: N(item?.netWeight),
});

const normalizeEntries = (item) => {
  const savedEntries = Array.isArray(item?.destinationEntries)
    ? item.destinationEntries
    : [];

  if (savedEntries.length === 0) return [createEntry(item)];

  return savedEntries.map((entry) => ({
    id: entry.id || createEntry(item).id,
    name: entry.name || "",
    pricePerUnit: N(entry.pricePerUnit),
    weight: N(entry.weight),
  }));
};

export default function SaleDestinationConfirmDialog({
  open,
  transaction,
  onClose,
  onConfirm,
}) {
  const [itemGroups, setItemGroups] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItemGroups(
      (transaction?.items || []).map((item) => ({
        item,
        entries: normalizeEntries(item),
      }))
    );
    setError("");
  }, [open, transaction]);

  const totals = useMemo(() => {
    return itemGroups.reduce(
      (result, group) => {
        const sendWeight = N(group.item?.netWeight);
        const sendAmount = sendWeight * N(group.item?.pricePerUnit);
        const destinationWeight = group.entries.reduce(
          (sum, entry) => sum + N(entry.weight),
          0
        );
        const destinationAmount = group.entries.reduce(
          (sum, entry) => sum + N(entry.weight) * N(entry.pricePerUnit),
          0
        );

        result.sendWeight += sendWeight;
        result.sendAmount += sendAmount;
        result.destinationWeight += destinationWeight;
        result.destinationAmount += destinationAmount;
        return result;
      },
      {
        sendWeight: 0,
        sendAmount: 0,
        destinationWeight: 0,
        destinationAmount: 0,
      }
    );
  }, [itemGroups]);

  const weightDifference = totals.destinationWeight - totals.sendWeight;
  const amountDifference = totals.destinationAmount - totals.sendAmount;

  const updateEntry = (groupIndex, entryId, field, value) => {
    setItemGroups((current) =>
      current.map((group, index) =>
        index !== groupIndex
          ? group
          : {
              ...group,
              entries: group.entries.map((entry) =>
                entry.id === entryId
                  ? {
                      ...entry,
                      [field]:
                        field === "name" ? value : Math.max(0, N(value)),
                    }
                  : entry
              ),
            }
      )
    );
    setError("");
  };

  const addEntry = (groupIndex) => {
    setItemGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? { ...group, entries: [...group.entries, createEntry(group.item)] }
          : group
      )
    );
    setError("");
  };

  const removeEntry = (groupIndex, entryId) => {
    setItemGroups((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              entries: group.entries.filter((entry) => entry.id !== entryId),
            }
          : group
      )
    );
  };

  const handleConfirm = async () => {
    const hasEmptyGroup = itemGroups.some((group) => group.entries.length === 0);
    const hasEmptyName = itemGroups.some((group) =>
      group.entries.some((entry) => !entry.name.trim())
    );

    if (hasEmptyGroup) {
      setError("กรุณาเพิ่มข้อมูลปลายทางอย่างน้อย 1 รายการต่อสินค้า");
      return;
    }
    if (hasEmptyName) {
      setError("กรุณากรอกชื่อรายการปลายทางให้ครบ");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onConfirm(
        itemGroups.map((group) => ({
          id: group.item.id,
          destinationEntries: group.entries.map((entry) => ({
            id: entry.id,
            name: entry.name.trim(),
            pricePerUnit: N(entry.pricePerUnit),
            weight: N(entry.weight),
          })),
        }))
      );
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          submitError?.response?.data?.error ||
          "ไม่สามารถบันทึกข้อมูลปลายทางได้"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800">
            ยืนยันน้ำหนักสินค้า
          </h2>
          <p className="mt-1 text-sm font-medium text-slate-400">
            บันทึกน้ำหนักและราคาที่ได้รับจากปลายทาง
          </p>
        </div>
        <IconButton onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent sx={{ p: 0 }}>
        <div className="max-h-[68vh] overflow-y-auto p-6">
          <div className="min-w-[940px] space-y-6">
            <div className="grid grid-cols-[minmax(280px,1fr)_180px_180px_200px] gap-3 px-3 text-sm font-black text-slate-400">
              <span>สินค้า / รายการ</span>
              <span className="text-right">ราคา (บาท/กก.)</span>
              <span className="text-right">น้ำหนัก (กก.)</span>
              <span className="text-right">มูลค่า (บาท)</span>
            </div>

            {itemGroups.map((group, groupIndex) => {
              const sendWeight = N(group.item?.netWeight);
              const sendPrice = N(group.item?.pricePerUnit);
              const productName =
                group.item?.productName || group.item?.product?.title || "-";

              return (
                <section
                  key={group.item.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                >
                  <div className="grid grid-cols-[minmax(280px,1fr)_180px_180px_200px] items-center gap-3 border-b border-amber-100 bg-amber-50 px-4 py-3 text-amber-700">
                    <div>
                      <div className="font-black">{productName}</div>
                      <div className="text-xs font-bold text-amber-500">
                        ข้อมูลตอนส่งสินค้า
                      </div>
                    </div>
                    <span className="text-right text-lg font-black">
                      {formatNumber(sendPrice)}
                    </span>
                    <span className="text-right text-lg font-black">
                      {formatNumber(sendWeight)}
                    </span>
                    <span className="text-right text-lg font-black">
                      {formatNumber(sendPrice * sendWeight)}
                    </span>
                  </div>

                  <div className="space-y-2 p-3">
                    {group.entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="grid grid-cols-[minmax(280px,1fr)_180px_180px_200px] items-center gap-3 rounded-xl border border-rose-100 bg-rose-50/30 p-2"
                      >
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => removeEntry(groupIndex, entry.id)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-rose-500 transition-colors hover:bg-rose-50"
                            title="ลบรายการ"
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </button>
                          <input
                            value={entry.name}
                            onChange={(event) =>
                              updateEntry(
                                groupIndex,
                                entry.id,
                                "name",
                                event.target.value
                              )
                            }
                            placeholder="ชื่อรายการปลายทาง"
                            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 font-bold text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={entry.pricePerUnit}
                          onChange={(event) =>
                            updateEntry(
                              groupIndex,
                              entry.id,
                              "pricePerUnit",
                              event.target.value
                            )
                          }
                          className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-right text-lg font-black text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={entry.weight}
                          onChange={(event) =>
                            updateEntry(
                              groupIndex,
                              entry.id,
                              "weight",
                              event.target.value
                            )
                          }
                          className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-right text-lg font-black text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                        />
                        <span className="pr-2 text-right text-lg font-black text-slate-700">
                          {formatNumber(N(entry.weight) * N(entry.pricePerUnit))}
                        </span>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addEntry(groupIndex)}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                      แก้ไข / เพิ่มรายการปลายทาง
                    </button>
                  </div>
                </section>
              );
            })}

            <div className="ml-auto w-[590px] space-y-2 border-t border-slate-200 pt-4">
              <div className="grid grid-cols-[1fr_180px_200px] gap-3 px-3 text-slate-500">
                <span className="font-bold">รวมตอนส่ง</span>
                <span className="text-right font-black">
                  {formatNumber(totals.sendWeight)}
                </span>
                <span className="text-right font-black">
                  {formatNumber(totals.sendAmount)}
                </span>
              </div>
              <div className="grid grid-cols-[1fr_180px_200px] gap-3 px-3 text-slate-700">
                <span className="font-bold">รวมปลายทาง</span>
                <span className="text-right font-black">
                  {formatNumber(totals.destinationWeight)}
                </span>
                <span className="text-right font-black">
                  {formatNumber(totals.destinationAmount)}
                </span>
              </div>
              <div
                className={`grid grid-cols-[1fr_180px_200px] gap-3 rounded-xl border px-3 py-3 ${
                  weightDifference >= 0 && amountDifference >= 0
                    ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                    : "border-rose-200 bg-rose-50 text-rose-600"
                }`}
              >
                <span className="font-black">ผลต่างทั้งหมด</span>
                <span
                  className={`text-right font-black ${
                    weightDifference >= 0
                      ? "text-emerald-600"
                      : "text-rose-600"
                  }`}
                >
                  {formatNumber(weightDifference)}
                </span>
                <span
                  className={`text-right font-black ${
                    amountDifference >= 0
                      ? "text-emerald-600"
                      : "text-rose-600"
                  }`}
                >
                  {formatNumber(amountDifference)}
                </span>
              </div>
            </div>

            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 font-bold text-rose-600">
                {error}
              </div>
            )}
          </div>
        </div>
      </DialogContent>

      <button
        type="button"
        onClick={handleConfirm}
        disabled={saving || itemGroups.length === 0}
        className="w-full bg-slate-800 py-5 text-xl font-black text-white transition-colors hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? "กำลังบันทึก..." : "ยืนยันน้ำหนักสินค้า"}
      </button>
    </Dialog>
  );
}
